/* -------------------------------------------------------------------------
 * Sayuri signature · gingko-fall  (Tokyo Dawn)
 * -------------------------------------------------------------------------
 * Replaces liquid-ripple as Tokyo Dawn's signature animation. Real-photo
 * gingko leaves drifting down across the viewport.
 *
 * Why gingko: the scent metaobject's place is "Tokyo · Aoyama". Aoyama-
 * dori and Meiji Jingu Gaien's gingko avenues are Tokyo's canonical
 * golden-hour image, late October through late November. The bright
 * yellow-gold palette aligns with the scent accent #C57838 (orange-rust)
 * better than any other autumn leaf — and gingko's unmistakable fan
 * shape keeps the animation visually distinct from maple-fall (Momiji),
 * sakura-petals (Blossoms of Spring), and the other 10 signatures.
 *
 * Pattern mirrors sayuri-maple-fall.js's PNG path: each particle is a
 * <picture>-less <img> stretched over its bounding div, falling on a
 * three-track wind path (left/center/right curve), sized 22–48px with
 * the size→duration coupling baked in (bigger leaves fall faster).
 * No procedural SVG fallback — the photos are the whole point.
 *
 * Asset manifest is exposed by snippets/sayuri-animation-layer.liquid as
 * window.SAYURI_TOKYO_DAWN_ASSETS.leaves when this signature is active.
 * Six photographic variants, generated via scripts/generate-tokyo-dawn-
 * assets.py against fal.ai's xai/grok-imagine-image endpoint.
 *
 * Scent character: "Tokyo at sunrise, citrus, soft floral, cardamom,
 * grounded woods." Calm, urban, golden hour. The leaves fall slowly —
 * none of liquid-ripple's twitchy expansion — and the warm color makes
 * the air feel lit by the first hour of light.
 * ----------------------------------------------------------------------- */
(function () {
  'use strict';
  var SA = window.SayuriAnimations;
  if (!SA) { return; }

  SA.register('gingko-fall', {
    mount: function (el, ctx) {
      // Tier counts mirror maple-fall — gives consistent particle density
      // across the suite. Lush tier only for merchant-tuned PDPs that
      // explicitly request more atmosphere.
      var n = ctx.intensity === 'lush' ? 16 : (ctx.intensity === 'whisper' ? 8 : 12);

      var assets = (typeof window !== 'undefined') && window.SAYURI_TOKYO_DAWN_ASSETS;
      var leafImages = (assets && assets.leaves) || [];
      if (!leafImages.length) {
        // No PNGs available — refuse to fall back to anything else.
        // Better to render nothing than a degraded version. Theme push
        // should always include the tokyo-leaf-*.png assets alongside
        // this script.
        return;
      }

      // Dedicated PRNG seed so re-mounts pick a stable distribution.
      var rand = ctx.seed('gingko-fall'.charCodeAt(0) * 131 + 13 * 7919);

      var frag = document.createDocumentFragment();
      for (var i = 0; i < n; i++) {
        var leafUrl = leafImages[Math.floor(rand() * leafImages.length)];

        var left = rand() * 100;            // 0–100vw
        var size = 22 + rand() * 26;        // 22–48px
        var delay = -rand() * 32;           // staggered start
        var opacity = 0.55 + rand() * 0.4;  // 0.55–0.95

        // Size → fall-speed coupling. Bigger leaves drop visibly faster;
        // smaller ones drift slowly. Maps size (22–48px) inversely to
        // duration with jitter so same-sized leaves don't lockstep.
        // Biggest ~16s, smallest ~30s — gingko is heavier than sakura
        // petals but lighter than maple leaves, so the band sits between.
        var sizeFactor = (size - 22) / 26;
        var dur = 16 + (1 - sizeFactor) * 12 + rand() * 2;

        // Three wind tracks (i % 3) — left curve, center drift, right
        // curve. Spin direction and rate are fully random per leaf.
        var fallVariant = i % 3;
        var spinVariant = rand() > 0.5 ? 0 : 1;
        var swayDur = 5 + rand() * 3;
        var spinDur = 6 + rand() * 8;

        var p = document.createElement('div');
        p.className = 'sayuri-gingko';
        p.style.position = 'absolute';
        p.style.top = '-40px';
        p.style.left = left + 'vw';
        p.style.width = size + 'px';
        p.style.height = size + 'px';
        p.style.opacity = opacity;
        p.style.animation =
          'gingko-fall-' + fallVariant + ' ' + dur + 's linear ' + delay + 's infinite, ' +
          'gingko-sway ' + swayDur + 's ease-in-out ' + delay + 's infinite, ' +
          'gingko-spin-' + spinVariant + ' ' + spinDur + 's linear ' + delay + 's infinite';

        var img = document.createElement('img');
        img.src = leafUrl;
        img.alt = '';
        img.setAttribute('aria-hidden', 'true');
        img.loading = 'lazy';
        img.decoding = 'async';
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'contain';
        img.style.display = 'block';
        p.appendChild(img);

        frag.appendChild(p);
      }
      el.appendChild(frag);
    },
    css: [
      // Three fall paths — same shapes as maple-fall for visual consistency.
      '@keyframes gingko-fall-0 {',
      '  0%   { transform: translate(0, -10vh); }',
      '  100% { transform: translate(-8vw, 110vh); }',
      '}',
      '@keyframes gingko-fall-1 {',
      '  0%   { transform: translate(0, -10vh); }',
      '  100% { transform: translate(4vw, 110vh); }',
      '}',
      '@keyframes gingko-fall-2 {',
      '  0%   { transform: translate(0, -10vh); }',
      '  100% { transform: translate(10vw, 110vh); }',
      '}',
      // Gentle horizontal sway, applied on top of the fall translate.
      '@keyframes gingko-sway {',
      '  0%, 100% { margin-left: 0; }',
      '  50%      { margin-left: 14px; }',
      '}',
      // Two spin directions. Gingko leaves spin faster than maple in
      // real life (smaller, lighter) — the spinDur range in mount()
      // covers that.
      '@keyframes gingko-spin-0 {',
      '  from { rotate: 0deg; }',
      '  to   { rotate: 360deg; }',
      '}',
      '@keyframes gingko-spin-1 {',
      '  from { rotate: 0deg; }',
      '  to   { rotate: -360deg; }',
      '}'
    ].join('')
  });
})();
